import { useEffect, useState } from 'react';
import styles from './ReportGrid.module.css';
import LoadIndicator from 'components/common/LoadIndicator/LoadIndicator';
import { getApi } from 'api/axios';
import ChartQueryBuilder from 'components/common/ChartQueryBuilder/ChartQueryBuilder';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DataGrid } from '@mui/x-data-grid';

const ReportGrid = ({ shopId, modifiers }) => {
  const [orderedDataLoading, setOrderedDataLoading] = useState(false);
  const [orderedData, setOrderedData] = useState([]);
  const [rows, setRows] = useState([]);
  const { t } = useTranslation();

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [rowCountState, setRowCountState] = useState(0);

  const columns = [
    { field: 'createdAt', headerName: t('orders.reportGrid.createdAt'), minWidth: 180, sortable: false },
    { field: 'orderId', headerName: t('orders.reportGrid.orderId'), minWidth: 100, sortable: false },
    { field: 'fullName', headerName: t('orders.reportGrid.fullName'), minWidth: 160, sortable: false },
    { field: 'emailAddress', headerName: t('orders.reportGrid.emailAddress'), minWidth: 250, sortable: false },
    { field: 'fileName', headerName: t('orders.reportGrid.fileName'), minWidth: 250, sortable: false },
    { field: 'mod1', headerName: t('orders.reportGrid.mod1'), minWidth: 160, sortable: false },
    { field: 'mod2', headerName: t('orders.reportGrid.mod2'), minWidth: 200, sortable: false },
    { field: 'quantity', headerName: t('orders.reportGrid.quantity'), type: 'number', width: 50, sortable: false },
    { field: 'schoolName', headerName: t('orders.reportGrid.schoolName'), minWidth: 150, sortable: false },
    { field: 'className', headerName: t('orders.reportGrid.className'), width: 85, sortable: false },
    { field: 'grossAmount', headerName: t('orders.reportGrid.grossAmount'), type: 'number', width: 75, sortable: false }
  ];

  const getUriId = (segment) => {
    return +segment.match('mod#id:[0-9]*')[0]?.split(':')[1];
  };

  const extractModNames = ({ modifier, isPrio1 }) => {
    const regex = /mod#id:(\d+)/g;
    const matches = [...modifier.matchAll(regex)].map((match) => Number(match[1]));

    if (!matches.length) return t('orders.reportGrid.unknown');

    return isPrio1
      ? t(`templates.productTypes.${modifiers?.find((el) => getUriId(el.modifierUriSegment) === matches[0])?.name}`) !==
        `templates.productTypes.${modifiers?.find((el) => getUriId(el.modifierUriSegment) === matches[0])?.name}`
        ? t(`templates.productTypes.${modifiers?.find((el) => getUriId(el.modifierUriSegment) === matches[0])?.name}`)
        : modifiers?.find((el) => getUriId(el.modifierUriSegment) === matches[0])?.name ||
          t('orders.reportGrid.unknown')
      : t(`templates.productTypes.${modifiers?.find((el) => getUriId(el.modifierUriSegment) === matches[1])?.name}`) !==
        `templates.productTypes.${modifiers?.find((el) => getUriId(el.modifierUriSegment) === matches[1])?.name}`
      ? t(`templates.productTypes.${modifiers?.find((el) => getUriId(el.modifierUriSegment) === matches[1])?.name}`)
      : modifiers?.find((el) => getUriId(el.modifierUriSegment) === matches[1])?.name || t('orders.reportGrid.unknown');
  };

  const fetchOrderedItems = async () => {
    try {
      setOrderedDataLoading(true);
      const res = await (
        await getApi({
          baseUrl: window._env_.REACT_APP_QDAK_PRODUCT_URL
        })
      ).get(
        `${`/admin/shops/${shopId}/orders/order-items/accounting`}${localStorage.getItem('reportorders')}${
          !localStorage.getItem('reportorders')
            ? `?size=${paginationModel.pageSize}&page=${paginationModel.page + 1}`
            : `&size=${paginationModel.pageSize}&page=${paginationModel.page + 1}`
        }`
      );

      if (res.status === 200) {
        setRowCountState(res.data.page.totalElements);
        setOrderedData(res.data._embedded.orderItemAccountingStatisticsDTOList);
      }
      setOrderedDataLoading(false);
    } catch (e) {
      console.log(e);
      setOrderedDataLoading(false);
    }
  };

  useEffect(() => {
    if (shopId) {
      fetchOrderedItems();
    }
  }, [shopId, paginationModel?.page, paginationModel.pageSize]);

  useEffect(() => {
    if (orderedData.length > 0 && modifiers.length > 0) {
      const formattedRows = orderedData.map((el, idx) => {
        const date = new Date(el.createdAt);
        const formattedDate = `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(
          date.getDate()
        ).padStart(2, '0')} ${date.toLocaleString('hu-HU', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        })}`;

        return {
          id: idx,
          createdAt: formattedDate,
          orderId: el?.orderId,
          fullName: el.fullName,
          emailAddress: el.emailAddress,
          fileName: el.fileName,
          mod1: extractModNames({ modifier: el.modifier, isPrio1: true }),
          mod2: extractModNames({ modifier: el.modifier, isPrio1: false }),
          quantity: el.quantity,
          schoolName: el.schoolName,
          className: el.className,
          grossAmount: el.grossAmount
        };
      });

      setRows(formattedRows);
    }
  }, [orderedData, modifiers]);

  return (
    <ChartQueryBuilder
      refetch={fetchOrderedItems}
      accountingData={orderedData}
      queryName={'reportorders'}
      isChart={false}
      isDownload={true}>
      <div className={styles.tableContainer}>
        {orderedDataLoading ? (
          <LoadIndicator color={'var(--primary-light)'} height={800} />
        ) : (
          <Box sx={{ height: 400, width: '100%' }}>
            <DataGrid
              disableColumnMenu
              disableSelectionOnClick
              disableColumnSelector
              disableColumnFilter
              columns={columns}
              rows={rows ?? []}
              paginationModel={paginationModel}
              rowCount={rowCountState}
              pageSizeOptions={[5, 10, 25, 50, 100]}
              paginationMode='server'
              onPaginationModelChange={setPaginationModel}
            />
          </Box>
        )}
      </div>
    </ChartQueryBuilder>
  );
};

export default ReportGrid;
